import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Space,
  Spin,
  message,
  Table,
  Tag,
  Modal,
  Alert,
  Typography,
  Tooltip,
  Empty,
  Descriptions,
  Row,
  Col,
  Divider,
  Result,
  Form,
  Input,
  InputNumber,
  DatePicker
} from 'antd';
import {
  FileTextOutlined,
  BankOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DownloadOutlined,
  SendOutlined,
  CreditCardOutlined,
  PlusOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import governanceService from '@services/operator/governanceService';
import ListPageHeader from '@shared/ListPageHeader';
import { formatDateTime, formatDate } from '@utils/dateTime';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';

const { Text, Paragraph } = Typography;

// Format Taiwan e-invoice number: "GL41000063" → "GL-41000063"
const formatEInvoiceNumber = (num) => {
  if (!num || num.length !== 10) return num;
  return `${num.slice(0, 2)}-${num.slice(2)}`;
};

const STATUS_COLORS = {
  draft: 'default',
  pending: 'processing',
  paid: 'success',
  partially_paid: 'warning',
  overdue: 'error',
  past_due: 'error',
  cancelled: 'default',
  refunded: 'purple',
  forgiven: 'cyan',
  merged: 'default'
};

const STATUS_ICONS = {
  draft: <FileTextOutlined />,
  pending: <ClockCircleOutlined />,
  paid: <CheckCircleOutlined />,
  partially_paid: <DollarOutlined />,
  overdue: <ExclamationCircleOutlined />,
  past_due: <ExclamationCircleOutlined />,
  cancelled: <FileTextOutlined />,
  refunded: <DollarOutlined />,
  forgiven: <CheckCircleOutlined />,
  merged: <FileTextOutlined />
};

const Invoices = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [bankTransferInfo, setBankTransferInfo] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [notifyModalVisible, setNotifyModalVisible] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyInvoice, setNotifyInvoice] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [isEnterprise, setIsEnterprise] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [response, subResponse] = await Promise.all([
        governanceService.getInvoices(),
        governanceService.getSubscription().catch(() => null)
      ]);

      if (subResponse?.success) {
        setIsEnterprise(subResponse.data.subscription?.pricing_plan?.plan_type === 'enterprise');
      }

      if (response.success) {
        setInvoices(response.data.invoices || []);
        setBankTransferInfo(response.data.bank_transfer_info);
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.error.fetch_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t, fallbackMessage: t('common.error.fetch_failed') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewDetail = async (invoice) => {
    setDetailModalVisible(true);
    setDetailLoading(true);

    try {
      const response = await governanceService.getInvoice(invoice.id);
      if (response.success) {
        setSelectedInvoice(response.data.invoice);
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.error.fetch_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t, fallbackMessage: t('common.error.fetch_failed') });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setDetailModalVisible(false);
    setSelectedInvoice(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success(t('common.message.copied'));
  };

  const handleOpenNotifyModal = (invoice) => {
    setNotifyInvoice(invoice);
    form.resetFields();
    form.setFieldsValue({
      transfer_date: dayjs(),
      transfer_amount: invoice.total
    });
    setNotifyModalVisible(true);
  };

  const handleCloseNotifyModal = () => {
    setNotifyModalVisible(false);
    setNotifyInvoice(null);
    form.resetFields();
  };

  const handleNotifyPayment = async () => {
    try {
      const values = await form.validateFields();
      setNotifyLoading(true);

      const response = await governanceService.notifyPayment(notifyInvoice.id, {
        bank_account_last5: values.bank_account_last5,
        transfer_date: values.transfer_date.format('YYYY-MM-DD'),
        transfer_amount: values.transfer_amount,
        notes: values.notes
      });

      if (response.success) {
        message.success(t('operator.governance.invoices.notify_payment_success'));
        handleCloseNotifyModal();
        fetchData(); // Refresh the list
      } else {
        handleApiError(response, {
          t,
          fallbackMessage: t('common.error.submit_failed')
        });
      }
    } catch (error) {
      if (Array.isArray(error?.errorFields)) return;
      handleNetworkError(error, {
        t,
        fallbackMessage: t('common.error.submit_failed')
      });
    } finally {
      setNotifyLoading(false);
    }
  };

  const handlePayInvoice = async (invoice) => {
    try {
      setPayLoading(true);
      const response = await governanceService.payInvoice(invoice.id);

      if (response.success) {
        message.success(t('operator.governance.invoices.payment_successful'));
        handleCloseDetail();
        fetchData();
      } else {
        handleApiError(response, {
          t,
          fallbackMessage: t('operator.governance.invoices.payment_failed')
        });
      }
    } catch (error) {
      handleNetworkError(error, {
        t,
        fallbackMessage: t('operator.governance.invoices.payment_failed')
      });
    } finally {
      setPayLoading(false);
    }
  };

  // 操作員只能自助取消 addon / credit_purchase 帳單；subscription/mixed/usage
  // 取消會造成漏帳，須走 admin（後端同步守衛，前端隱藏按鈕避免按了必失敗）。
  const operatorCancellable = (invoice) =>
    invoice?.status === 'pending' &&
    ['addon', 'credit_purchase'].includes(invoice?.invoice_type);

  const handleCancelInvoice = (invoice) => {
    Modal.confirm({
      title: t('operator.governance.invoices.cancel_confirm_title'),
      content: t('operator.governance.invoices.cancel_confirm_content', {
        invoice_number: invoice.invoice_number
      }),
      okText: t('operator.governance.invoices.cancel_confirm_ok'),
      okType: 'danger',
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        try {
          setCancelLoading(true);
          const response = await governanceService.cancelInvoice(invoice.id);

          if (response.success) {
            message.success(t('operator.governance.invoices.cancel_success'));
            handleCloseDetail();
            fetchData();
          } else {
            handleApiError(response, {
              t,
              fallbackMessage: t('operator.governance.invoices.cancel_failed')
            });
          }
        } catch (error) {
          handleNetworkError(error, {
            t,
            fallbackMessage: t('operator.governance.invoices.cancel_failed')
          });
        } finally {
          setCancelLoading(false);
        }
      }
    });
  };

  const columns = [
    {
      title: t('operator.governance.invoices.invoice_number'),
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      render: (text, record) => (
        <Button type="link" onClick={() => handleViewDetail(record)} style={{ padding: 0 }}>
          {text}
        </Button>
      )
    },
    {
      title: t('operator.governance.invoices.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={STATUS_COLORS[status]} icon={STATUS_ICONS[status]}>
          {t(`operator.governance.invoices.statuses.${status}`)}
        </Tag>
      )
    },
    {
      title: t('operator.governance.invoices.e_invoice_number'),
      key: 'e_invoice',
      render: (_, record) => {
        const eInv = record.e_invoice;
        if (!eInv) return '-';
        if (eInv.status === 'issued') {
          return (
            <Tooltip title={eInv.random_number ? `${t('operator.governance.invoices.e_invoice_random_number')}: ${eInv.random_number}` : undefined}>
              <Tag color="green" icon={<CheckCircleOutlined />}>
                {formatEInvoiceNumber(eInv.e_invoice_number)}
              </Tag>
            </Tooltip>
          );
        }
        if (eInv.status === 'pending') {
          return <Tag color="processing" icon={<ClockCircleOutlined />}>{t('operator.governance.invoices.e_invoice_pending')}</Tag>;
        }
        if (eInv.status === 'failed') {
          return <Tag color="error" icon={<ExclamationCircleOutlined />}>{t('operator.governance.invoices.e_invoice_failed')}</Tag>;
        }
        return '-';
      }
    },
    {
      title: t('operator.governance.invoices.period'),
      key: 'period',
      render: (_, record) => {
        if (!record.billing_period_start) return <Text>-</Text>;
        return (
          <Text>
            {formatDate(record.billing_period_start)} ~ {formatDate(record.billing_period_end)}
          </Text>
        );
      }
    },
    {
      title: t('operator.governance.invoices.total'),
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (total, record) => (
        <Text strong>
          {record.currency} {total.toLocaleString()}
        </Text>
      )
    },
    {
      title: t('operator.governance.invoices.due_date'),
      dataIndex: 'due_date',
      key: 'due_date',
      render: (date, record) => {
        if (!date) return '-';

        const isOverdue = record.overdue;
        return (
          <Text type={isOverdue ? 'danger' : undefined}>
            {formatDate(date)}
            {isOverdue && (
              <Tag color="red" style={{ marginLeft: 8 }}>
                {t('operator.governance.invoices.overdue_days', { days: record.days_overdue })}
              </Tag>
            )}
          </Text>
        );
      }
    },
    {
      title: t('operator.governance.invoices.issued_at'),
      dataIndex: 'issued_at',
      key: 'issued_at',
      render: (date) => date ? formatDateTime(date) : '-'
    },
    {
      title: t('common.actions'),
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            onClick={() => handleViewDetail(record)}
          >
            {t('common.button.view')}
          </Button>
          {!isEnterprise && (record.status === 'pending' || record.status === 'past_due') &&
            record.suggested_payment_method === 'credit_card' && (
            <Button
              type="primary"
              size="small"
              icon={<CreditCardOutlined />}
              loading={payLoading}
              onClick={() => handlePayInvoice(record)}
            >
              {t('operator.governance.invoices.pay_now_button')}
            </Button>
          )}
          {!isEnterprise && record.status === 'pending' &&
            record.suggested_payment_method === 'bank_transfer' &&
            bankTransferInfo?.available && (
            <Button
              size="small"
              icon={<SendOutlined />}
              onClick={() => handleOpenNotifyModal(record)}
            >
              {t('operator.governance.invoices.notify_payment_button')}
            </Button>
          )}
          {!isEnterprise && operatorCancellable(record) && (
            <Button
              size="small"
              danger
              icon={<CloseCircleOutlined />}
              loading={cancelLoading}
              onClick={() => handleCancelInvoice(record)}
            >
              {t('operator.governance.invoices.cancel_button')}
            </Button>
          )}
        </Space>
      )
    }
  ];

  // Check for pending invoices
  const pendingInvoice = invoices.find(inv => inv.status === 'pending');

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <ListPageHeader items={['governance', 'governance.invoices']} />

      {isEnterprise && (
        <Alert
          message={t('operator.governance.invoices.enterprise_notice')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Pending Invoice Alert - Credit Card */}
      {pendingInvoice && pendingInvoice.suggested_payment_method === 'credit_card' && !isEnterprise && (
        <Card style={{ marginBottom: 16 }}>
          <Alert
            message={t('operator.governance.invoices.pending_credit_card_payment_alert')}
            description={
              <div style={{ marginTop: 12 }}>
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label={t('operator.governance.invoices.invoice_number')}>
                    <Text copyable={{ text: pendingInvoice.invoice_number }}>
                      {pendingInvoice.invoice_number}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('operator.governance.invoices.amount_due')}>
                    <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
                      {pendingInvoice.currency} {pendingInvoice.total.toLocaleString()}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('operator.governance.invoices.due_date')}>
                    <Text type={pendingInvoice.overdue ? 'danger' : undefined}>
                      {formatDate(pendingInvoice.due_date)}
                    </Text>
                  </Descriptions.Item>
                </Descriptions>
                <div style={{ marginTop: 16 }}>
                  {pendingInvoice.has_payment_method ? (
                    <Button
                      type="primary"
                      icon={<CreditCardOutlined />}
                      loading={payLoading}
                      onClick={() => handlePayInvoice(pendingInvoice)}
                    >
                      {t('operator.governance.invoices.pay_now_button')}
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => navigate('/governance/payment_methods')}
                    >
                      {t('operator.governance.invoices.add_payment_method')}
                    </Button>
                  )}
                </div>
              </div>
            }
            type="info"
            showIcon
            icon={<CreditCardOutlined />}
          />
        </Card>
      )}

      {/* Pending Invoice Alert - Bank Transfer (ATM) */}
      {pendingInvoice && pendingInvoice.suggested_payment_method === 'bank_transfer' && bankTransferInfo?.available && !isEnterprise && (
        <Card
          style={{ marginBottom: 16 }}
          bodyStyle={{ backgroundColor: '#fffbe6' }}
        >
          <Alert
            message={t('operator.governance.invoices.pending_payment_alert')}
            description={
              <div style={{ marginTop: 12 }}>
                <Row gutter={24}>
                  <Col span={12}>
                    <Descriptions column={1} size="small" bordered>
                      <Descriptions.Item label={t('operator.governance.invoices.invoice_number')}>
                        <Text copyable={{ text: pendingInvoice.invoice_number }}>
                          {pendingInvoice.invoice_number}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label={t('operator.governance.invoices.amount_due')}>
                        <Text strong style={{ color: '#ff4d4f', fontSize: '16px' }}>
                          {pendingInvoice.currency} {pendingInvoice.total.toLocaleString()}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label={t('operator.governance.invoices.due_date')}>
                        <Text type={pendingInvoice.overdue ? 'danger' : undefined}>
                          {formatDate(pendingInvoice.due_date)}
                        </Text>
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                  <Col span={12}>
                    <Descriptions column={1} size="small" bordered>
                      <Descriptions.Item label={t('operator.governance.payment_methods.bank_name')}>
                        {bankTransferInfo.bank_name}
                      </Descriptions.Item>
                      <Descriptions.Item label={t('operator.governance.payment_methods.account_name')}>
                        {bankTransferInfo.account_name}
                      </Descriptions.Item>
                      <Descriptions.Item label={t('operator.governance.payment_methods.account_number')}>
                        <Space>
                          <Text strong style={{ fontSize: '16px' }}>
                            {bankTransferInfo.account_number}
                          </Text>
                          <Tooltip title={t('common.button.copy')}>
                            <Button
                              type="text"
                              icon={<CopyOutlined />}
                              onClick={() => copyToClipboard(bankTransferInfo.account_number)}
                            />
                          </Tooltip>
                        </Space>
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                </Row>
                <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
                  {t('operator.governance.invoices.bank_transfer_notes', { invoice_number: pendingInvoice.invoice_number })}
                </Paragraph>
                <div style={{ marginTop: 16 }}>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={() => handleOpenNotifyModal(pendingInvoice)}
                  >
                    {t('operator.governance.invoices.notify_payment_button')}
                  </Button>
                </div>
              </div>
            }
            type="warning"
            showIcon
            icon={<BankOutlined />}
          />
        </Card>
      )}

      {/* Invoices List */}
      <Card
        title={
          <Space>
            <FileTextOutlined />
            {t('operator.governance.invoices.list_title')}
          </Space>
        }
      >
        {invoices.length > 0 ? (
          <Table
            dataSource={invoices}
            columns={columns}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => t('common.pagination.total', { total })
            }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('operator.governance.invoices.no_invoices')}
          />
        )}
      </Card>

      {/* Invoice Detail Modal */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            {selectedInvoice?.invoice_number || t('operator.governance.invoices.detail_title')}
          </Space>
        }
        open={detailModalVisible}
        onCancel={handleCloseDetail}
        footer={[
          <Button key="close" onClick={handleCloseDetail}>
            {t('common.button.close')}
          </Button>,
          !isEnterprise && (selectedInvoice?.status === 'pending' || selectedInvoice?.status === 'past_due') &&
            selectedInvoice?.suggested_payment_method === 'credit_card' && (
            selectedInvoice?.has_payment_method ? (
              <Button
                key="pay"
                type="primary"
                icon={<CreditCardOutlined />}
                loading={payLoading}
                onClick={() => handlePayInvoice(selectedInvoice)}
              >
                {t('operator.governance.invoices.pay_now_button')}
              </Button>
            ) : (
              <Button
                key="add_payment"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  handleCloseDetail();
                  navigate('/governance/payment_methods');
                }}
              >
                {t('operator.governance.invoices.add_payment_method')}
              </Button>
            )
          ),
          !isEnterprise && selectedInvoice?.status === 'pending' &&
            selectedInvoice?.suggested_payment_method === 'bank_transfer' && (
            <Button
              key="notify"
              type="primary"
              icon={<SendOutlined />}
              onClick={() => {
                handleCloseDetail();
                handleOpenNotifyModal(selectedInvoice);
              }}
            >
              {t('operator.governance.invoices.notify_payment_button')}
            </Button>
          ),
          !isEnterprise && operatorCancellable(selectedInvoice) && (
            <Button
              key="cancel"
              danger
              icon={<CloseCircleOutlined />}
              loading={cancelLoading}
              onClick={() => handleCancelInvoice(selectedInvoice)}
            >
              {t('operator.governance.invoices.cancel_button')}
            </Button>
          )
        ].filter(Boolean)}
        width={700}
      >
        {detailLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : selectedInvoice ? (
          <>
            {/* Invoice Status */}
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <Tag color={STATUS_COLORS[selectedInvoice.status]} icon={STATUS_ICONS[selectedInvoice.status]} style={{ fontSize: '16px', padding: '4px 12px' }}>
                {t(`operator.governance.invoices.statuses.${selectedInvoice.status}`)}
              </Tag>
            </div>

            {/* Invoice Info */}
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label={t('operator.governance.invoices.invoice_number')}>
                <Text copyable>{selectedInvoice.invoice_number}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('operator.governance.invoices.invoice_type')}>
                {t(`operator.governance.invoices.types.${selectedInvoice.invoice_type}`)}
              </Descriptions.Item>
              {selectedInvoice.billing_period_start && (
                <Descriptions.Item label={t('operator.governance.invoices.period')} span={2}>
                  {formatDate(selectedInvoice.billing_period_start)} ~ {formatDate(selectedInvoice.billing_period_end)}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('operator.governance.invoices.issued_at')}>
                {selectedInvoice.issued_at ? formatDateTime(selectedInvoice.issued_at) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('operator.governance.invoices.due_date')}>
                {selectedInvoice.due_date ? formatDate(selectedInvoice.due_date) : '-'}
              </Descriptions.Item>
              {selectedInvoice.paid_at && (
                <Descriptions.Item label={t('operator.governance.invoices.paid_at')} span={2}>
                  {formatDateTime(selectedInvoice.paid_at)}
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* E-Invoice Info */}
            {selectedInvoice.e_invoice && (
              <>
                <Divider>{t('operator.governance.invoices.e_invoice_section')}</Divider>
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label={t('operator.governance.invoices.e_invoice_status')}>
                    {selectedInvoice.e_invoice.status === 'issued' ? (
                      <Tag color="green" icon={<CheckCircleOutlined />}>
                        {t('operator.governance.invoices.e_invoice_issued')}
                      </Tag>
                    ) : selectedInvoice.e_invoice.status === 'pending' ? (
                      <Tag color="processing" icon={<ClockCircleOutlined />}>
                        {t('operator.governance.invoices.e_invoice_pending')}
                      </Tag>
                    ) : (
                      <Tag color="error" icon={<ExclamationCircleOutlined />}>
                        {t('operator.governance.invoices.e_invoice_failed')}
                      </Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('operator.governance.invoices.e_invoice_provider')}>
                    {selectedInvoice.e_invoice.provider === 'ecpay' ? 'ECPay' : selectedInvoice.e_invoice.provider}
                  </Descriptions.Item>
                  {selectedInvoice.e_invoice.e_invoice_number && (
                    <Descriptions.Item label={t('operator.governance.invoices.e_invoice_number')}>
                      <Text copyable strong>
                        {formatEInvoiceNumber(selectedInvoice.e_invoice.e_invoice_number)}
                      </Text>
                    </Descriptions.Item>
                  )}
                  {selectedInvoice.e_invoice.random_number && (
                    <Descriptions.Item label={t('operator.governance.invoices.e_invoice_random_number')}>
                      <Text>{selectedInvoice.e_invoice.random_number}</Text>
                    </Descriptions.Item>
                  )}
                  {selectedInvoice.e_invoice.carrier_type && (
                    <Descriptions.Item label={t('operator.governance.invoices.e_invoice_carrier')}>
                      {selectedInvoice.e_invoice.carrier_type}
                    </Descriptions.Item>
                  )}
                  {selectedInvoice.e_invoice.issued_at && (
                    <Descriptions.Item label={t('operator.governance.invoices.e_invoice_issued_at')}>
                      {formatDateTime(selectedInvoice.e_invoice.issued_at)}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </>
            )}

            {/* Invoice Items */}
            {selectedInvoice.items && selectedInvoice.items.filter(item => item.item_type !== 'tax').length > 0 && (
              <>
                <Divider>{t('operator.governance.invoices.items')}</Divider>
                <Table
                  dataSource={selectedInvoice.items.filter(item => item.item_type !== 'tax')}
                  columns={(() => {
                    // 檢查是否有 usage 類型的項目（需要顯示數量和單價）
                    const hasUsageItems = selectedInvoice.items.some(item => item.item_type === 'usage');

                    const baseColumns = [
                      {
                        title: t('operator.governance.invoices.item_description'),
                        dataIndex: 'description',
                        key: 'description'
                      }
                    ];

                    // 只有當有 usage 類型項目時才顯示數量和單價欄位
                    if (hasUsageItems) {
                      baseColumns.push(
                        {
                          title: t('operator.governance.invoices.item_quantity'),
                          dataIndex: 'quantity',
                          key: 'quantity',
                          align: 'right',
                          render: (val, record) => record.item_type === 'usage' ? val : '-'
                        },
                        {
                          title: t('operator.governance.invoices.item_unit_price'),
                          dataIndex: 'unit_price',
                          key: 'unit_price',
                          align: 'right',
                          render: (val, record) => record.item_type === 'usage'
                            ? `${selectedInvoice.currency} ${val.toLocaleString()}`
                            : '-'
                        }
                      );
                    }

                    baseColumns.push({
                      title: t('operator.governance.invoices.item_amount'),
                      dataIndex: 'amount',
                      key: 'amount',
                      align: 'right',
                      render: (val) => (
                        <Text type={val < 0 ? 'success' : undefined}>
                          {selectedInvoice.currency} {val.toLocaleString()}
                        </Text>
                      )
                    });

                    return baseColumns;
                  })()}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </>
            )}

            {/* Totals */}
            <Divider />
            <Row justify="end">
              <Col span={10}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label={t('operator.governance.invoices.subtotal')}>
                    <Text>{selectedInvoice.currency} {selectedInvoice.subtotal.toLocaleString()}</Text>
                  </Descriptions.Item>
                  {selectedInvoice.tax_amount > 0 && (
                    <Descriptions.Item label={t('operator.governance.invoices.tax')}>
                      <Text>{selectedInvoice.currency} {selectedInvoice.tax_amount.toLocaleString()}</Text>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label={t('operator.governance.invoices.total')}>
                    <Text strong style={{ fontSize: '18px' }}>
                      {selectedInvoice.currency} {selectedInvoice.total.toLocaleString()}
                    </Text>
                  </Descriptions.Item>
                  {selectedInvoice.outstanding_amount > 0 && (
                    <>
                      {selectedInvoice.paid_amount > 0 && (
                        <Descriptions.Item label={t('operator.governance.invoices.paid_amount')}>
                          <Text type="success">{selectedInvoice.currency} {selectedInvoice.paid_amount.toLocaleString()}</Text>
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label={t('operator.governance.invoices.outstanding')}>
                        <Text type="danger">{selectedInvoice.currency} {selectedInvoice.outstanding_amount.toLocaleString()}</Text>
                      </Descriptions.Item>
                    </>
                  )}
                </Descriptions>
              </Col>
            </Row>

            {/* Bank Transfer Info for Pending Invoice (ATM only) */}
            {selectedInvoice.status === 'pending' && selectedInvoice.suggested_payment_method === 'bank_transfer' && bankTransferInfo?.available && !isEnterprise && (
              <>
                <Divider>{t('operator.governance.invoices.payment_info')}</Divider>
                <Alert
                  message={t('operator.governance.invoices.bank_transfer_title')}
                  description={
                    <Descriptions column={1} size="small" style={{ marginTop: 8 }}>
                      <Descriptions.Item label={t('operator.governance.payment_methods.bank_name')}>
                        {bankTransferInfo.bank_name}
                      </Descriptions.Item>
                      <Descriptions.Item label={t('operator.governance.payment_methods.account_name')}>
                        {bankTransferInfo.account_name}
                      </Descriptions.Item>
                      <Descriptions.Item label={t('operator.governance.payment_methods.account_number')}>
                        <Text copyable strong style={{ fontSize: '16px' }}>
                          {bankTransferInfo.account_number}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label={t('operator.governance.payment_methods.notes')}>
                        <Text type="warning">
                          {t('operator.governance.invoices.bank_transfer_notes', { invoice_number: selectedInvoice.invoice_number })}
                        </Text>
                      </Descriptions.Item>
                    </Descriptions>
                  }
                  type="info"
                  showIcon
                  icon={<BankOutlined />}
                />
              </>
            )}
          </>
        ) : null}
      </Modal>

      {/* Payment Notification Modal */}
      <Modal
        title={
          <Space>
            <SendOutlined />
            {t('operator.governance.invoices.notify_payment_title')}
          </Space>
        }
        open={notifyModalVisible}
        onCancel={handleCloseNotifyModal}
        onOk={handleNotifyPayment}
        confirmLoading={notifyLoading}
        okText={t('operator.governance.invoices.submit_notify')}
        cancelText={t('common.button.cancel')}
        width={500}
      >
        {notifyInvoice && (
          <>
            <Alert
              message={t('operator.governance.invoices.notify_payment_info')}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label={t('operator.governance.invoices.invoice_number')}>
                {notifyInvoice.invoice_number}
              </Descriptions.Item>
              <Descriptions.Item label={t('operator.governance.invoices.amount_due')}>
                <Text strong>{notifyInvoice.currency} {notifyInvoice.total.toLocaleString()}</Text>
              </Descriptions.Item>
            </Descriptions>

            <Form
              form={form}
              layout="vertical"
            >
              <Form.Item
                name="bank_account_last5"
                label={t('operator.governance.invoices.bank_account_last5')}
                rules={[
                  { required: true, message: t('operator.governance.invoices.bank_account_last5_required') },
                  { pattern: /^\d{5}$/, message: t('operator.governance.invoices.bank_account_last5_invalid') }
                ]}
              >
                <Input
                  maxLength={5}
                  placeholder="12345"
                  style={{ width: 120 }}
                />
              </Form.Item>

              <Form.Item
                name="transfer_date"
                label={t('operator.governance.invoices.transfer_date')}
                rules={[
                  { required: true, message: t('operator.governance.invoices.transfer_date_required') }
                ]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  disabledDate={(current) => current && current > dayjs().endOf('day')}
                />
              </Form.Item>

              <Form.Item
                name="transfer_amount"
                label={t('operator.governance.invoices.transfer_amount')}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={0}
                  formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>

              <Form.Item
                name="notes"
                label={t('operator.governance.invoices.notify_notes')}
              >
                <Input.TextArea
                  rows={2}
                  placeholder={t('operator.governance.invoices.notify_notes_placeholder')}
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Invoices;
